import { useEffect, useMemo, useState } from 'react'
import DataTable from '../../components/DataTable'
import Meter from '../../components/Meter'
import PageHeader from '../../components/PageHeader'
import { getLicenseInfo } from './organisationsService'
import { licenseInformationStrings as S } from './strings'
import './organisations.css'

/**
 * One quantity that is being consumed: how much is left, out of how much, with
 * the bar between them.
 *
 * The fill warms as the headroom goes — the same reading الرخصة gives — so the
 * page says "you are fine" or "you are close" without anyone reading a number.
 */
function Reading({ label, used, total, remaining, remainingLabel, usedLabel }) {
  const known = total != null && used != null
  const spent = known && total > 0 ? Math.round((used / total) * 100) : null
  const tone = spent == null ? 'neutral' : spent >= 85 ? 'danger' : spent >= 60 ? 'warning' : 'success'

  return (
    <div className="org-reading">
      <div className="org-reading__head">
        <bdi className="org-reading__label">{label}</bdi>
        {spent != null && <span className="org-reading__spent">{spent}%</span>}
      </div>

      <p className="org-reading__figure">
        <bdi className="org-reading__value">
          {remaining == null ? '—' : remaining.toLocaleString('en-US')}
        </bdi>
        <bdi className="org-reading__scale">
          / {total == null ? '—' : total.toLocaleString('en-US')}
        </bdi>
      </p>

      <Meter value={spent} tone={tone} size="sm" label={label} />

      <dl className="org-reading__facts">
        <div>
          <dt>
            <bdi>{remainingLabel}</bdi>
          </dt>
          <dd>{remaining == null ? '—' : remaining.toLocaleString('en-US')}</dd>
        </div>
        <div>
          <dt>
            <bdi>{usedLabel}</bdi>
          </dt>
          <dd>{used == null ? '—' : used.toLocaleString('en-US')}</dd>
        </div>
      </dl>
    </div>
  )
}

/**
 * معلومات الترخيص — Views/LicenseInformation/Index.cshtml.
 *
 * The legacy has no menu entry for this page and nothing links to it; it is reached by
 * typing the URL, which is why the route exists but the rail does not list it.
 */
export default function LicenseInformationPage() {
  const [data, setData] = useState(null)

  useEffect(() => {
    getLicenseInfo().then(setData)
  }, [])

  const values = useMemo(() => {
    const license = data?.license
    const daysLeft = license?.expirationDate
      ? Math.max(
          0,
          Math.ceil((new Date(`${license.expirationDate}T00:00:00`).getTime() - Date.now()) / 86400000),
        )
      : null

    return {
      company: data?.company?.arabicName ?? null,
      licenseType: license?.licenseTypeName ?? null,
      startDate: license?.startDate ?? null,
      duration: license?.duration ?? null,
      daysLeft,
      expirationDate: license?.expirationDate ?? null,
      accounts: license?.usersNumber ?? null,
      consumedAccounts: license?.consumedAccounts ?? null,
      remainingAccounts:
        license?.usersNumber == null ? null : license.usersNumber - license.consumedAccounts,
    }
  }, [data])

  const historyColumns = useMemo(
    () => [
      { key: 'licenseType', header: S.history.licenseType },
      { key: 'startDate', header: S.history.startDate },
      { key: 'duration', header: S.history.duration },
      { key: 'accounts', header: S.history.accounts },
    ],
    [],
  )

  return (
    <>
      <PageHeader title={S.title} subtitle={S.subtitle} />

      {/*
       * Nine equal grey tiles said nothing about which figure mattered — and
       * three of them (500 accounts / 125 used / 375 left) are one proportion
       * printed as three unrelated numbers, which is the exact thing Meter was
       * written to replace. الرخصة already reads this data correctly; this page
       * now reads it the same way.
       */}
      <section className="org-panel">
        <h3 className="org-panel__title">
          <bdi>{S.current}</bdi>
        </h3>

        <div className="org-license">
          <p className="org-license__id">
            <bdi className="org-license__client">{values.company ?? S.unavailable}</bdi>
            <bdi className="org-license__kind">{values.licenseType ?? S.unavailable}</bdi>
          </p>

          <div className="org-license__readings">
            <Reading
              label={S.fields.accounts}
              used={values.consumedAccounts}
              total={values.accounts}
              remaining={values.remainingAccounts}
              remainingLabel={S.fields.remainingAccounts}
              usedLabel={S.fields.consumedAccounts}
            />
            <Reading
              label={S.fields.duration}
              used={
                values.duration == null || values.daysLeft == null
                  ? null
                  : values.duration - values.daysLeft
              }
              total={values.duration}
              remaining={values.daysLeft}
              remainingLabel={S.fields.daysLeft}
              usedLabel={S.elapsed}
            />
          </div>

          <dl className="org-license__facts">
            {['startDate', 'expirationDate'].map((key) => (
              <div key={key} className="org-license__fact">
                <dt>
                  <bdi>{S.fields[key]}</bdi>
                </dt>
                <dd>
                  <bdi dir="ltr">{values[key] ?? S.unavailable}</bdi>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <DataTable
        columns={historyColumns}
        rows={data?.history ?? []}
        loading={!data}
        title={S.previous}
        getRowId={(row, index) => index}
      />

      {/* THE RENEW PANEL IS GONE. A file picker and a save button, and the button had no
          handler — so choosing a licence file lit up a control that did nothing. It was
          the most convincing dead control in the app, because it looks like a finished
          upload flow.

          Renewing a licence IS possible, on `/companies/license`, which reads the file and
          posts it to `/admin/licensemanagement/updatelicense`. This screen is the
          read-only licence INFORMATION page and has no endpoint of its own; sending
          someone to the screen that works beats a form that does not. COSMETIC_UI.md. */}
    </>
  )
}
